import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Pencil, Copy, XCircle, Shield } from 'lucide-react';

/**
 * ManageRunSheet
 * Bottom-sheet drawer that holds secondary/administrative actions for a run.
 *
 * Visible items depend on capabilities passed in:
 *   - canEdit / canClone / canCancel   → host actions
 *   - canAdminCancel                   → admin/scout-only action
 *
 * Destructive actions live here so they no longer crowd the main page.
 */
const ManageRunSheet = ({
  open,
  onOpenChange,
  canEdit,
  canClone,
  canCancel,
  canAdminCancel,
  onEdit,
  onClone,
  onCancel,
  onAdminCancel,
  noun = 'Run',
}) => {
  const itemClass =
    'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-zinc-50 hover:bg-zinc-100 active:bg-zinc-200 active:scale-[0.99] transition-all text-left';
  const dangerItemClass =
    'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-red-50 hover:bg-red-100 active:bg-red-200 active:scale-[0.99] transition-all text-left';

  const handle = (fn) => () => {
    onOpenChange(false);
    // small defer so the sheet close animation runs before any modal opens
    setTimeout(() => fn?.(), 50);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl pb-8 sm:max-w-md sm:mx-auto"
        data-testid="manage-run-sheet"
      >
        <SheetHeader className="text-left">
          <SheetTitle>Manage {noun}</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-2">
          {canEdit && (
            <button
              type="button"
              onClick={handle(onEdit)}
              className={itemClass}
              data-testid="manage-edit-btn"
            >
              <Pencil className="w-5 h-5 text-zinc-700" />
              <span className="text-[15px] font-medium text-zinc-800">Edit {noun}</span>
            </button>
          )}

          {canClone && (
            <button
              type="button"
              onClick={handle(onClone)}
              className={itemClass}
              data-testid="manage-duplicate-btn"
            >
              <Copy className="w-5 h-5 text-zinc-700" />
              <span className="text-[15px] font-medium text-zinc-800">Duplicate {noun}</span>
            </button>
          )}

          {canCancel && (
            <button
              type="button"
              onClick={handle(onCancel)}
              className={dangerItemClass}
              data-testid="manage-cancel-btn"
            >
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="text-[15px] font-medium text-red-700">Cancel This {noun}</span>
            </button>
          )}

          {canAdminCancel && (
            <button
              type="button"
              onClick={handle(onAdminCancel)}
              className={dangerItemClass}
              data-testid="manage-admin-cancel-btn"
            >
              <Shield className="w-5 h-5 text-red-600" />
              <span className="text-[15px] font-medium text-red-700">Cancel This {noun} (Admin)</span>
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ManageRunSheet;
